import { memo, useEffect, useMemo, useState } from 'react';
import { roundBook, fmtBook } from '../../utils/casinoBook';
import Collapse from '../Collapse';

function findByNatAndType(sub, nat, subtype) {
  return sub.find((s) => s.nat === nat && s.subtype === subtype);
}

function isSusp(item) {
  return !item || item.gstatus === 'SUSPENDED' || item.gstatus === '0';
}

function suspCls(item) {
  return isSusp(item) ? ' suspended' : '';
}

// 500000 -> 5L, 50000 -> 50K, 1000 -> 1K (reference range formatting)
function formatAmount(n) {
  const num = Number(n) || 0;
  if (num >= 100000) return `${num / 100000}L`;
  if (num >= 1000) return `${num / 1000}K`;
  return String(num);
}

// Extract Odd/Even odds from the nested odds array on card items
function getCardOdds(cardItem) {
  const odds = cardItem?.odds || [];
  const oddEntry = odds.find((o) => o.nat === 'Odd') || odds[0];
  const evenEntry = odds.find((o) => o.nat === 'Even') || odds[1];
  return {
    oddVal: oddEntry?.b || 0,
    evenVal: evenEntry?.b || 0,
  };
}

// Book (exposure) — sid-scoped keys first (same naam do markets me ho sakta
// hai: Player A main + consecutive), phir nat fallback
function bookOf(exposures, item) {
  const v = exposures?.[`sid:${item?.sid}`] ?? exposures?.[item?.nat];
  return v ? roundBook(v) : 0;
}

function bookCls(v) {
  if (!v) return 'book-black';
  return v < 0 ? 'book-red' : 'book-green';
}

function BookSpan({ exposures, item }) {
  const v = bookOf(exposures, item);
  return <span className={`mr-2 casino-book ${bookCls(v)}`}>{fmtBook(v)}</span>;
}

// Info icon + R:<min>-<max> tooltip (bootstrap collapse replica — click pe
// khulta/band hota hai). variant 'main' = icon-range collapse + collapsed
// icon (reference main/consecutive rows), 'plain' = collapse icon-range
// (reference Odd/Even rows).
function RangeInfo({ id, item, open, onToggle, variant = 'main' }) {
  const iconClass = variant === 'main'
    ? `fas fa-info-circle${open ? '' : ' collapsed'}`
    : variant === 'mobile'
      ? 'fas fa-info-circle float-right'
      : 'fas fa-info-circle';

  return (
    <>
      <i
        data-toggle="collapse"
        data-target={`#${id}`}
        {...(variant === 'main' ? { 'aria-expanded': open ? 'true' : 'false' } : {})}
        className={iconClass}
        onClick={onToggle}
        style={{ cursor: 'pointer' }}
      ></i>
      {' '}
      <Collapse id={id} open={open} className="icon-range">
        {'\n                          R:'}
        <span>{formatAmount(item?.min)}</span>
        -
        <span>{formatAmount(item?.max)}</span>
      </Collapse>
    </>
  );
}

// ─── Desktop layout (d-none-small) ───────────────────────────────────────────

const DesktopLayout = memo(function DesktopLayout({ sub, onBet, exposures }) {
  const [openRanges, setOpenRanges] = useState({});
  const toggle = (id) => setOpenRanges((p) => ({ ...p, [id]: !p[id] }));

  const playerA = findByNatAndType(sub, 'Player A', 'teen');
  const playerB = findByNatAndType(sub, 'Player B', 'teen');
  const conA = findByNatAndType(sub, 'Player A', 'con');
  const conB = findByNatAndType(sub, 'Player B', 'con');

  const cardItems = useMemo(() => {
    const items = [];
    for (let i = 1; i <= 6; i++) {
      items.push(findByNatAndType(sub, `Card ${i}`, 'oddeven'));
    }
    return items;
  }, [sub]);

  const backLayRow = (label, item, rangeId, extraNameCls = '') => (
    <div className="casino-box-row">
      <div className={`casino-nation-name${extraNameCls}`}>
        <b>{label}</b>
        {' '}
        <div className="float-right">
          <BookSpan exposures={exposures} item={item} />
          {' '}
          <RangeInfo id={rangeId} item={item} open={!!openRanges[rangeId]} onToggle={() => toggle(rangeId)} />
        </div>
      </div>
      {' '}
      <div className="casino-bl-box">
        <div className={`back casino-bl-box-item${suspCls(item)}`} onClick={isSusp(item) ? undefined : () => onBet(item.sid, 'back', item.b, item)}>
          <span className="casino-box-odd">{item?.b || 0}</span>
        </div>
        {' '}
        <div className={`lay casino-bl-box-item${suspCls(item)}`} onClick={isSusp(item) ? undefined : () => onBet(item.sid, 'lay', item.l, item)}>
          <span className="casino-box-odd">{item?.l || 0}</span>
        </div>
      </div>
    </div>
  );

  const oddEvenRow = (label, rangeId, pickVal, natSuffix) => (
    <div className={`casino-box-row${label === 'Odd' ? ' mb-3' : ''}`}>
      <div className="casino-nation-name mb-4">
        <b>{label}</b>
        {' '}
        <div className="float-right">
          <RangeInfo id={rangeId} item={cardItems.find(Boolean)} open={!!openRanges[rangeId]} onToggle={() => toggle(rangeId)} variant="plain" />
        </div>
      </div>
      {' '}
      {cardItems.map((item, i) => {
        const { oddVal, evenVal } = getCardOdds(item);
        const val = pickVal === 'odd' ? oddVal : evenVal;
        const susp = isSusp(item) || !val;
        return (
          <div key={item?.sid ?? i} className="casino-bl-box">
            <div
              className={`back casino-bl-box-item${susp ? ' suspended' : ''}`}
              onClick={susp ? undefined : () => onBet(item.sid, 'back', val, { ...item, nat: `${item.nat} ${natSuffix}`, b: val })}
            >
              <span className="casino-box-odd">{val}</span>
            </div>
            {' '}
            <div className={`casino-book ${bookCls(bookOf(exposures, item))}`}>{fmtBook(bookOf(exposures, item))}</div>
          </div>
        );
      })}
    </div>
  );

  return (
    <div className="teen1daycasino-container d-none-small">
      {/* LEFT: Player A */}
      <div className="teen1dayleft">
        <div className="casino-box-row">
          <div className="casino-nation-name no-border casino-bl-box-title">
            <div className="playera">Player A</div>
          </div>
          {' '}
          <div className="casino-bl-box casino-bl-box-title">
            <div className="casino-bl-box-item"><b>Back</b></div>
            {' '}
            <div className="casino-bl-box-item"><b>Lay</b></div>
          </div>
        </div>
        {' '}
        {backLayRow('Main', playerA, 'range1')}
        {' '}
        {backLayRow('Consecutive', conA, 'range6', ' casino-card-img')}
      </div>

      {/* CENTER divider */}
      {' '}
      <div className="teen1daycenter"></div>
      {' '}

      {/* RIGHT: Player B */}
      <div className="teen1dayright">
        <div className="casino-box-row">
          <div className="casino-nation-name no-border casino-bl-box-title">
            <div className="playerb">Player B</div>
          </div>
          {' '}
          <div className="casino-bl-box casino-bl-box-title">
            <div className="casino-bl-box-item"><b>Back</b></div>
            {' '}
            <div className="casino-bl-box-item"><b>Lay</b></div>
          </div>
        </div>
        {' '}
        {backLayRow('Main', playerB, 'range7')}
        {' '}
        {backLayRow('Consecutive', conB, 'range12', ' casino-card-img')}
      </div>

      {/* CARDS: Odd / Even across Card 1-6 */}
      {' '}
      <div className="teen1dayother">
        <div className="casino-box-row">
          <div className="casino-nation-name no-border"></div>
          {' '}
          {cardItems.map((item, i) => (
            <div key={item?.sid ?? i} className="casino-bl-box">
              <div className="casino-bl-box-item"><b>{`Card ${i + 1}`}</b></div>
            </div>
          ))}
        </div>
        {' '}
        {oddEvenRow('Odd', 'range13', 'odd', 'Odd')}
        {' '}
        {oddEvenRow('Even', 'range14', 'even', 'Even')}
      </div>
    </div>
  );
});

// ─── Mobile layout (d-none-big) ──────────────────────────────────────────────

const MobileLayout = memo(function MobileLayout({ sub, onBet, exposures }) {
  const [openRanges, setOpenRanges] = useState({});
  const toggle = (id) => setOpenRanges((p) => ({ ...p, [id]: !p[id] }));

  const mainPlayers = sub.filter((s) => s.subtype === 'teen');
  const consecutive = sub.filter((s) => s.subtype === 'con');
  const oddEvenCards = useMemo(() => {
    const items = [];
    for (let i = 1; i <= 6; i++) {
      const c = findByNatAndType(sub, `Card ${i}`, 'oddeven');
      if (c) items.push(c);
    }
    return items;
  }, [sub]);

  const mobileBook = (item) => {
    const v = bookOf(exposures, item);
    return <span className={`float-right ${bookCls(v)}`}>{fmtBook(v)}</span>;
  };

  const backLayGroup = (label, items, rangeId, titleExtraCls = '') => (
    <div className="casino-box-row">
      <div className="casino-bl-box casino-bl-box-title">
        <div className={`casino-bl-box-item${titleExtraCls}`}>
          <b>{label}</b>
          {' '}
          <div className="float-right">
            <RangeInfo id={rangeId} item={items[0]} open={!!openRanges[rangeId]} onToggle={() => toggle(rangeId)} variant="mobile" />
          </div>
        </div>
        {' '}
        <div className="casino-bl-box-item"><b>Back</b></div>
        {' '}
        <div className="casino-bl-box-item"><b>Lay</b></div>
      </div>
      {' '}
      {items.map((item) => (
        <div key={item.sid} className="casino-bl-box">
          <div className="casino-bl-box-item casino-odds-name">
            <span>{item.nat}</span>
            {' '}
            {mobileBook(item)}
          </div>
          {' '}
          <div className={`back casino-bl-box-item${suspCls(item)}`} onClick={isSusp(item) ? undefined : () => onBet(item.sid, 'back', item.b, item)}>
            <span className="casino-box-odd">{item.b || 0}</span>
          </div>
          {' '}
          <div className={`lay casino-bl-box-item${suspCls(item)}`} onClick={isSusp(item) ? undefined : () => onBet(item.sid, 'lay', item.l, item)}>
            <span className="casino-box-odd">{item.l || 0}</span>
          </div>
        </div>
      ))}
    </div>
  );

  return (
    <div className="teen1daycasino-container d-none-big">
      {backLayGroup('Main', mainPlayers, 'range1')}
      {' '}
      {backLayGroup('Consecutive', consecutive, 'range6', ' casino-card-img')}
      {' '}
      {/* CARDS ODD/EVEN */}
      <div className="casino-box-row teen1dayodev">
        <div className="casino-bl-box casino-bl-box-title">
          <div className="casino-bl-box-item casino-card-img">
            <b>Cards</b>
            {' '}
            <div className="float-right">
              <RangeInfo id="range7" item={oddEvenCards[0]} open={!!openRanges.range7} onToggle={() => toggle('range7')} variant="mobile" />
            </div>
          </div>
          {' '}
          <div className="casino-bl-box-item"><b>Odd</b></div>
          {' '}
          <div className="casino-bl-box-item"><b>Even</b></div>
        </div>
        {' '}
        {oddEvenCards.map((item) => {
          const { oddVal, evenVal } = getCardOdds(item);
          const bookVal = bookOf(exposures, item);
          return (
            <div key={item.sid} className="casino-bl-box">
              <div className="casino-bl-box-item casino-odds-name">
                <span>{item.nat}</span>
              </div>
              {' '}
              <div className={`back casino-bl-box-item${isSusp(item) || !oddVal ? ' suspended' : ''}`} onClick={isSusp(item) || !oddVal ? undefined : () => onBet(item.sid, 'back', oddVal, { ...item, nat: `${item.nat} Odd`, b: oddVal })}>
                <span className="casino-box-odd">{oddVal}</span>
                {' '}
                <span className={bookCls(bookVal)}>{fmtBook(bookVal)}</span>
              </div>
              <div className={`back casino-bl-box-item${isSusp(item) || !evenVal ? ' suspended' : ''}`} onClick={isSusp(item) || !evenVal ? undefined : () => onBet(item.sid, 'back', evenVal, { ...item, nat: `${item.nat} Even`, b: evenVal })}>
                <span className="casino-box-odd">{evenVal}</span>
                {' '}
                <span className={bookCls(bookVal)}>{fmtBook(bookVal)}</span>
              </div>
            </div>
          );
        })}
      </div>
    </div>
  );
});

// ─── Main export ─────────────────────────────────────────────────────────────

const BetTableTeen62 = memo(function BetTableTeen62({ gameData, onBet, exposures }) {
  const sub = gameData?.sub || [];
  const handleBet = onBet || (() => {});
  const [isMobile, setIsMobile] = useState(() => typeof window !== 'undefined' && window.innerWidth < 768);

  useEffect(() => {
    const onResize = () => setIsMobile(window.innerWidth < 768);
    window.addEventListener('resize', onResize);
    return () => window.removeEventListener('resize', onResize);
  }, []);

  // Reference me ek waqt pe ek hi layout DOM me hota hai (dusra v-if se
  // <!---->), CSS-hide nahi
  return isMobile
    ? <MobileLayout sub={sub} onBet={handleBet} exposures={exposures} />
    : <DesktopLayout sub={sub} onBet={handleBet} exposures={exposures} />;
});

export default BetTableTeen62;
